import React, {FC, memo, useState} from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  tableCellClasses,
  Modal,
} from '@mui/material';

import eyeOff from 'assets/icons/eyeOff.svg';
import {CampaignDto} from 'store/models/campaign';
import {IMetadata} from 'store/models/common';
import {ISortConfig} from 'hooks/useSortColumn';

import {
  styledTableBody,
  styledTableHead,
  styledTableRow,
  tableContainer,
  tableHeadCell,
} from 'styles/MUIStyles/table';

import {
  tableMoreDotsMenu,
  tableMoreDotsMenuPaper,
} from 'styles/MUIStyles/common';

import {
  ArrowAsc,
  ArrowDesc,
  DefaultSortIcon,
  DeleteIcon,
  EditIcon,
  MoreIcon,
  CloseIcon,
  SuccessNotification,
} from 'assets/icons';

import {
  snackbarAlertContent,
  snackbarSuccessAlert,
} from 'styles/MUIStyles/snackbars';

// Модалки
import EditCampaignModal from './modals/EditCampaignModal';
import DeleteCampaignModal from './modals/DeleteCampaignModal';
import HideCampaignModal from './modals/HideCampaignModal';
import dayjs from 'dayjs';
import TableEmptyState from 'components/TableEmptyState/TableEmptyState';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: styledTableHead,
  borderBottom: '1px solid var(--border)',
  [`&.${tableCellClasses.body}`]: styledTableBody,
}));

const StyledTableRow = styled(TableRow)(() => styledTableRow);

interface Props {
  campaigns: CampaignDto[];
  metaData: IMetadata | null;
  sortConfig: ISortConfig;
  handleColumnClick: (column: string) => void;
}

const CampaignsTable: FC<Props> = memo(
  ({campaigns, metaData, sortConfig, handleColumnClick}) => {
    const [clickedCampaign, setClickedCampaign] = useState<CampaignDto | null>(
      null,
    );

    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [hideOpen, setHideOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState('');

    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const closeSnackbar = () => setSnackbarOpen(false);

    const openMenu = (e: any, c: CampaignDto) => {
      e.stopPropagation();
      setClickedCampaign(c);
      setMenuAnchor(e.currentTarget);
    };

    const closeMenu = () => setMenuAnchor(null);

    return (
      <div style={{height: '100%'}}>
        {/* TABLE */}
        <TableContainer component={Paper} sx={tableContainer}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {/* IMAGE */}
                <StyledTableCell>
                  <Box sx={tableHeadCell}>
                    <span>Image</span>
                  </Box>
                </StyledTableCell>

                {/* NAME */}
                <StyledTableCell>
                  <Box sx={tableHeadCell}>
                    <span>Name</span>
                  </Box>
                </StyledTableCell>

                {/* LINK */}
                <StyledTableCell>
                  <Box sx={tableHeadCell}>
                    <span>Link</span>
                  </Box>
                </StyledTableCell>

                {/* CREATED */}
                <StyledTableCell>
                  <Box sx={tableHeadCell}>
                    <span>Created date</span>
                  </Box>
                </StyledTableCell>

                {/* MODIFIED */}
                <StyledTableCell>
                  <Box sx={tableHeadCell}>
                    <span>Modified date</span>
                  </Box>
                </StyledTableCell>

                {/* ACTIONS */}
                <StyledTableCell>
                  <Box sx={tableHeadCell}>
                    <span>Actions</span>
                  </Box>
                </StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
            {!campaigns?.length ? <TableEmptyState /> : null}
              {campaigns.map((c) => (
                <StyledTableRow key={c.id}>
                  {/* IMAGE PREVIEW */}
                  <StyledTableCell>
                    {c.imageUrl ? (
                      <img
                        src={c.imageUrl}
                        alt="preview"
                        style={{
                          width: 60,
                          height: 40,
                          objectFit: 'cover',
                          borderRadius: 6,
                          cursor: 'pointer',
                          border: '1px solid var(--border)',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewImage(c.imageUrl!);
                        }}
                      />
                    ) : (
                      <span style={{color: 'var(--text-secondary)'}}>—</span>
                    )}
                  </StyledTableCell>

                  {/* NAME */}
                  <StyledTableCell>{c.name}</StyledTableCell>

                  {/* LINK */}
                  <StyledTableCell>
                    <a
                      href={c.link}
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {c.link}
                    </a>
                  </StyledTableCell>

                  {/* CREATED */}
                  <StyledTableCell>
                    {c.createdDateTime &&
                      dayjs(c.createdDateTime).format('DD.MM.YYYY HH:mm')}
                  </StyledTableCell>

                  {/* MODIFIED */}
                  <StyledTableCell>
                    {c.lastModifiedDateTime &&
                      dayjs(c.lastModifiedDateTime).format('DD.MM.YYYY HH:mm')}
                  </StyledTableCell>

                  {/* ACTIONS */}
                  <StyledTableCell>
                    <Button onClick={(e) => openMenu(e, c)}>
                      <MoreIcon />
                    </Button>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          sx={tableMoreDotsMenuPaper}
        >
          <MenuItem
            sx={tableMoreDotsMenu}
            onClick={() => {
              closeMenu();
              setEditOpen(true);
            }}
          >
            <EditIcon />
            Edit
          </MenuItem>

          <MenuItem
            sx={tableMoreDotsMenu}
            onClick={() => {
              closeMenu();
              setHideOpen(true);
            }}
          >
            <img width={'16px'} src={eyeOff} alt="" />
            Hide
          </MenuItem>

          <MenuItem
            sx={{...tableMoreDotsMenu, marginBottom: 0}}
            onClick={() => {
              closeMenu();
              setDeleteOpen(true);
            }}
          >
            <DeleteIcon />
            Remove
          </MenuItem>
        </Menu>

        {/* FULLSCREEN IMAGE MODAL */}
        <Modal
          open={Boolean(previewImage)}
          onClose={() => setPreviewImage(null)}
        >
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.85)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              cursor: 'zoom-out',
              p: 2,
            }}
            onClick={() => setPreviewImage(null)}
          >
            <img
              src={previewImage || ''}
              alt="full"
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                borderRadius: 8,
                boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                animation: 'fadeIn 0.25s ease',
              }}
            />
          </Box>
        </Modal>

        {/* MODALS */}
        <EditCampaignModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          campaign={clickedCampaign}
          onSuccess={(msg) => {
            setSnackbarMsg(msg);
            setSnackbarOpen(true);
          }}
        />

        <DeleteCampaignModal
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          campaign={clickedCampaign}
          onSuccess={(msg) => {
            setSnackbarMsg(msg);
            setSnackbarOpen(true);
          }}
        />

        <HideCampaignModal
          open={hideOpen}
          onClose={() => setHideOpen(false)}
          campaign={clickedCampaign}
          onSuccess={(msg) => {
            setSnackbarMsg(msg);
            setSnackbarOpen(true);
          }}
        />

        {/* SNACKBAR */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2500}
          onClose={closeSnackbar}
        >
          <Alert icon={false} sx={snackbarSuccessAlert}>
            <Box sx={snackbarAlertContent}>
              <SuccessNotification />
              {snackbarMsg}
              <span onClick={closeSnackbar} style={{cursor: 'pointer'}}>
                <CloseIcon />
              </span>
            </Box>
          </Alert>
        </Snackbar>
      </div>
    );
  },
);

export default CampaignsTable;
